import { DemoPage } from '@carboncopyco/components';
import {
  selectShowtimeDomain,
  useGetOrganizationQuery,
} from '@carboncopyco/data';
import type { IDemo } from '@carboncopyco/types';
import {
  AnalyticsTrackEventName,
  useAnalytics,
  useWorkspaceAnalytics,
} from '@carboncopyco/utils';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import GiveADemoModalExtended from '../../components/GiveADemoModalExtended';
import WorkspaceLayout from '../../components/Workspaces/WorkspaceLayout';
import { useCustomNavigate } from '../../hooks/useCustomNavigate';
import { useDemoCommons } from '../../hooks/useDemoCommons';
import { useOpenDemoEditorTab } from '../../screens/utils/openDemoEditorTab';
import { useUser, useWorkspaces } from '../../store/context';
import { useSelector } from 'react-redux';

export const DemoScreen = ({ demo }: { demo: IDemo }) => {
  const showtimeDomain = useSelector(selectShowtimeDomain);
  const { trackEvent } = useAnalytics();
  const { activeWorkspace, switchWorkspace } = useWorkspaces();
  const { activeOrganization } = useUser();
  const { data: organization } = useGetOrganizationQuery({
    id: String(activeOrganization.id),
  });

  const { trackDemoClickOpenCopyToWorkspaceModal } =
    useWorkspaceAnalytics('demo_page');
  const openDemoEditorTab = useOpenDemoEditorTab();
  const [openActionsDropdown, setOpenActionsDropdown] = useState(false);

  const {
    navigateToTourCapture,
    navigateToDemoPage,
    navigateToDemoPageByWorkspace,
    navigateToWorkspace,
    navigateToDemoShares,
    navigateToFolder,
    links,
  } = useCustomNavigate();
  const {
    handlePresent,
    workspace,
    workspaceId,
    demoId,
    organizationId,
    showCreatePresentation,
    setShowCreatePresentation,
    goToDemoPreview,
    handleCreatePresentation,
  } = useDemoCommons({ demo });

  const { search } = useLocation();

  if (workspace && activeWorkspace.current.id !== workspace.id) {
    switchWorkspace(workspace);
  }

  const goToEditor = (url?: string) => {
    if (!demo) return;
    setOpenActionsDropdown(false);
    openDemoEditorTab(demo, activeOrganization.id, url);
    trackEvent(AnalyticsTrackEventName.DEMO_CLICK_OPEN_INFO, {
      location: 'demo_page',
      demoId: demo.id,
    });
  };

  const toggleActionsDropdown = () =>
    setOpenActionsDropdown((prevState) => !prevState);

  useEffect(() => {
    const searchParams = new URLSearchParams(search);
    const giveADemoParam = searchParams.get('giveADemo');
    if (giveADemoParam) {
      handleCreatePresentation();
    }
  }, []);

  if (!demo || !demoId || !workspaceId || !organization) {
    return null;
  }

  return (
    <WorkspaceLayout>
      <DemoPage
        rootPath={links.getDemoPageRoot(Number(demoId))}
        workspaceId={workspaceId}
        organizationId={organizationId}
        demoId={demoId}
        updateDemo={() => {}}
        goToDemoPreview={goToDemoPreview}
        handleCreatePresentation={handleCreatePresentation}
        goToEditor={goToEditor}
        open={openActionsDropdown}
        onOpenChange={toggleActionsDropdown}
        handleCreateTour={async () => {
          trackEvent(AnalyticsTrackEventName.DEMO_CLICK_CREATE_TOUR, {
            location: 'demo_page',
            demoId: demo?.id,
          });
          setOpenActionsDropdown(false);
          if (demo) {
            await navigateToTourCapture({
              demoId: demo.id,
              folderId: demo.folderId,
            });
          }
        }}
        handleShareSettings={() => {}}
        goBack={() => navigateToWorkspace(workspaceId)}
        goToDuplicatedDemo={({ id }) =>
          navigateToDemoPage(String(id), workspaceId)
        }
        goToCopiedDemo={(id, targetWorkspaceId) => {
          trackDemoClickOpenCopyToWorkspaceModal({
            workspace,
          });
          navigateToDemoPageByWorkspace(String(id), targetWorkspaceId);
        }}
        goToWorkspacePage={() => navigateToWorkspace(demo?.workspaceId)}
        goToFolderPage={navigateToFolder}
        clonePath={links.getClonePageLink(demo?.recordId?.toString() || '')}
        goToPresentation={handlePresent}
        goToLinksTab={() => navigateToDemoPage(demoId, workspaceId, 'links')}
        onLearnMore={() =>
          window.dsDesktop.shellOpenExternal('https://www.demostack.com/')
        }
        onViewShares={() => navigateToDemoShares(demo?.id)}
        readOnly={!!demo?.readOnly}
      />
      {showCreatePresentation && demo && (
        <GiveADemoModalExtended
          open
          workspaceId={workspaceId}
          entity={demo}
          onPresent={() => setShowCreatePresentation(false)}
          onSave={() => {
            navigateToDemoPage(demoId, workspaceId, 'presentations');
            setShowCreatePresentation(false);
          }}
          onClose={() => setShowCreatePresentation(false)}
        />
      )}
    </WorkspaceLayout>
  );
};
